import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { MDVIPLogo } from '@mdvip/shared/components/blocks/MDVIPLogo';
import { Avatar, AvatarFallback } from '@mdvip/shared/components/ui/avatar';
import { Button, buttonVariants } from '@mdvip/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@mdvip/shared/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@mdvip/shared/components/ui/navigation-menu';
import { useFontSize } from '@mdvip/shared/hooks/useFontSize';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

const NAV_ITEMS = [
  { to: '/', icon: 'layoutGrid', labelKey: 'nav.dashboard' },
  { to: '/members', icon: 'users', labelKey: 'nav.members' },
  { to: '/messages', icon: 'messageSquare', labelKey: 'nav.messages', testId: 'nav-messages-button' },
  { to: '/resources', icon: 'bookOpen', labelKey: 'nav.resources' },
  { to: '/staff', icon: 'userCog', labelKey: 'nav.staff', mobileOnly: true as const },
] as const;

export function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();
  const { t } = useTranslation('providers');

  const handleLogout = (): void => {
    void logout();
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center px-4">
          <div className="flex items-center">
            <MDVIPLogo className="h-7 w-auto text-primary" />
          </div>

          <NavigationMenu className="ml-8 hidden md:flex">
            <NavigationMenuList className="items-center space-x-1">
              {NAV_ITEMS.filter((item) => !('mobileOnly' in item)).map((item) => (
                <NavigationMenuItem key={item.to}>
                  <NavigationMenuLink
                    render={<Link to={item.to} data-testid={'testId' in item ? item.testId : undefined} />}
                    data-active={isActive(item.to) || undefined}
                    className={cn(
                      buttonVariants({ variant: isActive(item.to) ? 'secondary' : 'ghost' }),
                      'flex items-center gap-2',
                    )}
                  >
                    <Icon name={item.icon} />
                    <span>{t(item.labelKey)}</span>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          <div className="ml-auto flex items-center space-x-2">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                render={
                  <Button
                    data-testid="user-menu-trigger"
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full ring-2 ring-primary/20 hover:ring-primary/40 transition-all"
                  />
                }
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64" align="end">
                <DropdownMenuLabel className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
                  {t('nav.practiceManagement')}
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate('/practice')} className="cursor-pointer py-2">
                  <Icon name="building" className="mr-2.5 text-primary" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{t('nav.practiceSettings')}</span>
                    <span className="text-xs text-muted-foreground">{t('nav.practiceSettingsDesc')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/staff')} className="cursor-pointer py-2">
                  <Icon name="userCog" className="mr-2.5 text-primary" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{t('nav.staffManagement')}</span>
                    <span className="text-xs text-muted-foreground">{t('nav.staffManagementDesc')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Icon name="textSize" size="xs" />
                    <span>{t('nav.fontSize.label', { size: fontSize })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={decreaseFontSize}
                      disabled={fontSize <= 85}
                      className="h-6 w-6 p-0"
                      title={t('nav.fontSize.smaller')}
                    >
                      <Icon name="minus" size="xs" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetFontSize}
                      disabled={fontSize === 100}
                      className="h-6 w-6 p-0"
                      title={t('nav.fontSize.reset')}
                    >
                      <Icon name="refreshCw" size="xs" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={increaseFontSize}
                      disabled={fontSize >= 130}
                      className="h-6 w-6 p-0"
                      title={t('nav.fontSize.larger')}
                    >
                      <Icon name="plus" size="xs" />
                    </Button>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-menu-item" onClick={handleLogout} className="cursor-pointer py-2">
                  <Icon name="logOut" className="mr-2" />
                  <span>{t('nav.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center justify-around h-16 px-2">
          {NAV_ITEMS.map((item) => (
            <Button
              key={item.to}
              render={<Link to={item.to} />}
              data-testid={'testId' in item ? `${item.testId}-mobile` : undefined}
              variant={isActive(item.to) ? 'secondary' : 'ghost'}
              className="flex flex-col items-center gap-1 h-auto py-2 px-3"
            >
              <Icon name={item.icon} size="xl" />
              <span className="text-xs">{t(item.labelKey)}</span>
            </Button>
          ))}
        </div>
      </nav>
    </>
  );
}
